/**
 * Search Results Page
 * Shows search results for assets and projects with pagination and filters
 */

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  IconButton,
} from '@mui/material';
import {
  ChevronLeft as BackIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

import Fuse from 'fuse.js';
import AppBar from '../elements/AppBar';
import PageBody from '../elements/PageBody';
import PageTitle from '../elements/PageTitle';
import FilterSection from '../elements/FilterSection';
import useProjectSelector from '../../hooks/useProjectSelector';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setQuery, setSearchType, clearSearch } from '../../store/slices/searchSlice';
import * as api from '../../services/api';

const FUSE_OPTIONS = {
  keys: ['name', 'unitNumber', 'uuid', 'description', { name: 'typeName', weight: 0.5 }],
  threshold: 0.3,
  includeScore: true,
  minMatchCharLength: 1,
};

const ITEMS_PER_PAGE = 20;

const SearchResultsPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const { query, searchType } = useAppSelector((state) => state.search);
  const { activeCustomer } = useAppSelector((state) => state.customer);
  const { assetTypes: assetTypeList } = useAppSelector((state) => state.asset);
  const { ProjectSelectorComponent } = useProjectSelector();

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [allAssets, setAllAssets] = useState([]);
  
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const debounceRef = useRef(null);

  // Clear the global search query when leaving the search page
  useEffect(() => {
    return () => {
      dispatch(clearSearch());
    };
  }, [dispatch]);

  const typeMap = useMemo(() => {
    const m = {};
    for (const t of (assetTypeList || [])) m[t.id] = t.name;
    return m;
  }, [assetTypeList]);

  useEffect(() => {
    if (!activeCustomer?.id) return;
    setLoading(true);
    api.getAssetsByCustomer(activeCustomer.id).then((r) => {
      const assets = (r.items || []).map(a => ({
        ...a,
        typeName: a.type?.name || typeMap[a.assetTypeId] || '',
      }));
      setAllAssets(assets);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [activeCustomer?.id, typeMap]);

  const fuse = useMemo(() => new Fuse(allAssets, FUSE_OPTIONS), [allAssets]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim()) {
      setResults([]);
      setTotal(0);
      setTotalPages(1);
      return;
    }

    debounceRef.current = setTimeout(() => {
      let items = fuse.search(query).map(r => r.item);

      if (statusFilter) {
        items = items.filter(i => i.status?.toLowerCase() === statusFilter.toLowerCase());
      }
      if (typeFilter && searchType === 'assets') {
        items = items.filter(i => i.assetTypeId === typeFilter || i.type?.id === typeFilter);
      }

      setTotal(items.length);
      setTotalPages(Math.ceil(items.length / ITEMS_PER_PAGE));
      const offset = (page - 1) * ITEMS_PER_PAGE;
      setResults(items.slice(offset, offset + ITEMS_PER_PAGE));
    }, 300);
  }, [query, fuse, page, statusFilter, typeFilter, searchType]);

  // Reset page when query or type changes
  useEffect(() => {
    setPage(1);
  }, [query, searchType, statusFilter, typeFilter]);

  const handleSearchChange = (e) => {
    dispatch(setQuery(e.target.value));
  };

  const handleTabChange = (_, newValue) => {
    dispatch(setSearchType(newValue === 0 ? 'assets' : 'projects'));
    setStatusFilter('');
    setTypeFilter('');
  };

  const handleResultClick = (item) => {
    if (searchType === 'assets') {
      navigate(`/asset/${item.id}`);
    } else {
      navigate(`/projects/${item.id}`);
    }
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setTypeFilter('');
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveFilters = statusFilter || typeFilter;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        showProjectSelector
        projectSelectorProps={{
          ProjectSelectorComponent,
        }}
      />
      
      <PageBody>
        {/* Back Button */}
        <Button
          variant="text"
          color="secondary"
          startIcon={<BackIcon />}
          onClick={() => navigate('/')}
          sx={{ height: 44, mb: 2 }}
        >
          Back to Map
        </Button>

        {/* Title Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <PageTitle>
            Search Results
          </PageTitle>
        </Box>

        {/* Search Input and Filters */}
        <FilterSection>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search assets, projects..."
              value={query}
              onChange={handleSearchChange}
              autoFocus
              size="small"
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: query && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => dispatch(setQuery(''))}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="deployed">Deployed</MenuItem>
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
              </Select>
            </FormControl>
            
            {searchType === 'assets' && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Asset Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Asset Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {(assetTypeList || []).map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {hasActiveFilters && (
              <Button
                size="small"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
                sx={{ height: 40 }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        </FilterSection>

        {/* Tabs */}
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={searchType === 'assets' ? 0 : 1}
            onChange={handleTabChange}
          >
            <Tab label="Assets" />
            <Tab label="Projects" />
          </Tabs>
        </Box>

        {/* Status */}
        <Box sx={{ mb: 2 }}>
          <Typography color="text.secondary">
            {loading ? 'Loading...' : query ? `${total} result${total !== 1 ? 's' : ''} found` : 'Enter a search term'}
          </Typography>
        </Box>

        {/* Results */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : results.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  {query ? 'No results found' : 'Enter a search term above to get started'}
                </Typography>
                {hasActiveFilters && query && (
                  <Button 
                    size="small" 
                    onClick={handleClearFilters}
                    sx={{ mt: 1 }}
                  >
                    Clear filters and try again
                  </Button>
                )}
              </Box>
            ) : (
              <>
                <List disablePadding>
                  {results.map((item, index) => (
                    <ListItem
                      key={item.id}
                      disablePadding
                      divider={index < results.length - 1}
                    >
                      <ListItemButton onClick={() => handleResultClick(item)}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography>
                                {item.name || item.unitNumber || 'Unnamed'}
                              </Typography>
                              {item.type && (
                                <Chip
                                  label={item.type.name || item.type}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {item.status && (
                                <Chip
                                  label={item.status}
                                  size="small"
                                  color={item.status === 'active' ? 'success' : 'default'}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            searchType === 'assets'
                              ? `UUID: ${item.uuid || '-'} | ${item.description || ''}`
                              : `Client: ${item.client || '-'} | ${item.description || ''}`
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </PageBody>
    </Box>
  );
};

export default SearchResultsPage;
